import React, { useState, useMemo, useContext, useEffect } from 'react';
import { useQueryClient } from 'react-query';
import { omitBy, isNil, values, isEmpty } from 'lodash';

import {
  queryKeys as profilesQueryKeys,
  useCreateProfile,
  useUpdateProfile,
} from '~/App/Api/Profile';
import { Contact } from '~/types/models';
import ContactModal from './ContactModal';
import FlashContext from '~/App/Components/Flash/Context';
import SearchModal from './SearchModal';
import { ProfileFormErrors } from './ProfileForm';
import useAnalytics from '~/react-hooks/use-analytics';
import {
  ADD_CONTACT,
  ADD_CONTACT_CONFIRM,
  EDIT_CONTACT,
  EDIT_CONTACT_SAVE,
} from '~/generated/AnalyticsTypes';

export interface ManageContactModalProps {
  initialValue: Contact | null;
  open: boolean;
  loading?: boolean;
  onRequestClose: () => void;
  onContactSave?: () => void;
  onContactAssign?: (contact: Contact) => void;
  searchModalHeader?: string;
  ctaText?: string;
  editHeader?: string;
  addHeader?: string;
}

export default function ManageContactModal({
  initialValue,
  open,
  loading,
  onRequestClose,
  onContactAssign,
  onContactSave,
  searchModalHeader = 'Add a contact',
  ctaText = 'Add contact',
  editHeader = 'Edit contact',
  addHeader = 'Add a contact',
}: ManageContactModalProps) {
  const initialErrors: ProfileFormErrors = { name: '' };
  const [contact, setContact] = useState<Contact | null>(null);
  const [showSearchContactModal, setShowSearchContactModal] = useState(false);
  const [showContactModal, setShowContactModal] = useState(false);
  const [errors, setErrors] = useState(initialErrors);
  const { createFlash } = useContext(FlashContext);
  const queryClient = useQueryClient();
  const analytics = useAnalytics();
  const isUpdateMode = useMemo(() => initialValue !== null, [initialValue]);

  const createErrorFlash = (
    id: string,
    message = 'Something went wrong while processing your request. Please try again.'
  ) =>
    createFlash({
      id,
      title: 'Error',
      message,
      options: { style: 'danger' },
    });

  const invalidateQuery = () => {
    void queryClient.invalidateQueries(profilesQueryKeys.lists());
  };

  const { mutateAsync: createProfile, isLoading: isSaving } = useCreateProfile({
    onSuccess: (data) => {
      invalidateQuery();
      onContactAssign?.(data);

      analytics.track(ADD_CONTACT_CONFIRM, data);

      onRequestClose?.();
    },
    onError: () => {
      createErrorFlash('create-profile-failed');
    },
  });

  const { mutateAsync: updateProfile, isLoading: isUpdating } =
    useUpdateProfile({
      onSuccess: (data) => {
        invalidateQuery();
        onContactSave?.();

        analytics.track(EDIT_CONTACT_SAVE, data);

        onRequestClose?.();
      },
      onError: () => {
        createErrorFlash('update-profile-failed');
      },
    });

  const heading = isUpdateMode ? editHeader : addHeader;
  const confirmButtonText = isUpdateMode ? 'Save' : ctaText;

  const handleProfileChange = (contact: Contact) => {
    Object.keys(errors).forEach((field) => {
      const updatedErrors = { ...errors };
      updatedErrors[field] =
        contact[field] === '' ? 'This field is required' : '';
      setErrors(updatedErrors);
    });
    setContact(contact);
  };

  const handleConfirmClick = () => {
    if (contact === null) return;

    const payload = omitBy<Contact>(contact, (v) => isNil(v) || isEmpty(v));

    if (isUpdateMode) {
      void updateProfile(payload);
    } else {
      void createProfile(payload);
    }
  };

  const closeSearchModal = () => setShowSearchContactModal(false);

  const openContactModal = () => {
    setShowContactModal(true);
    closeSearchModal();
  };

  useEffect(() => {
    if (!open) {
      closeSearchModal();
      setShowContactModal(false);
      setErrors(initialErrors);
      setContact(initialValue);
      return;
    }

    if (isUpdateMode) {
      openContactModal();

      analytics.track(EDIT_CONTACT);
    } else {
      setShowSearchContactModal(true);

      analytics.track(ADD_CONTACT);
    }
  }, [open, isUpdateMode]);

  useEffect(() => {
    setContact(initialValue);
  }, [initialValue]);

  const hasError = !values(errors).every(isEmpty);
  const isLoading = isSaving || isUpdating || loading;
  const isDisabled = hasError || contact === null;

  return (
    <>
      <SearchModal
        open={showSearchContactModal}
        heading={searchModalHeader}
        onModalClose={onRequestClose}
        onAddContactModalOpen={openContactModal}
        loading={loading}
        onContactAdd={onContactAssign}
      />
      <ContactModal
        onProfileChange={handleProfileChange}
        contact={contact}
        open={showContactModal}
        onCloseClick={onRequestClose}
        confirmButtonText={confirmButtonText}
        heading={heading}
        onConfirmClick={handleConfirmClick}
        loading={isLoading}
        disableSaveButton={isDisabled}
        errors={errors}
      />
    </>
  );
}
